import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiError, TOKEN_KEY, REFRESH_KEY, TAILLEUR_KEY } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [tailleur, setTailleur] = useState(() => {
    try {
      const raw = localStorage.getItem(TAILLEUR_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [ready, setReady] = useState(false);

  const persist = useCallback((data) => {
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(REFRESH_KEY, data.refresh_token);
    localStorage.setItem(TAILLEUR_KEY, JSON.stringify(data.tailleur));
    setTailleur(data.tailleur);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setReady(true);
      return;
    }
    api
      .get("/auth/me")
      .then(({ data }) => {
        setTailleur(data);
        localStorage.setItem(TAILLEUR_KEY, JSON.stringify(data));
      })
      .catch((e) => {
        // Keep offline session; only logout on a real auth rejection.
        if (e?.response?.status === 401) logout();
      })
      .finally(() => setReady(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const register = useCallback(
    async (payload) => {
      const { data } = await api.post("/auth/register", payload);
      persist(data);
      return data.tailleur;
    },
    [persist]
  );

  const login = useCallback(
    async (payload) => {
      const { data } = await api.post("/auth/login", payload);
      persist(data);
      return data.tailleur;
    },
    [persist]
  );

  const resetPin = useCallback(
    async (payload) => {
      const { data } = await api.post("/auth/reset-pin", payload);
      persist(data);
      return data.tailleur;
    },
    [persist]
  );

  const updateTailleur = useCallback((patch) => {
    setTailleur((prev) => {
      const next = { ...prev, ...patch };
      localStorage.setItem(TAILLEUR_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  function logout() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(TAILLEUR_KEY);
    setTailleur(null);
  }

  return (
    <AuthContext.Provider
      value={{ tailleur, ready, register, login, resetPin, logout, updateTailleur, apiError }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
